import { collection, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';
import { db } from '../../config';
import { shareScope, firestoreBatchLimit } from '../../../../constants/enum';
import type { ShareScope } from '../../../../types/pattern';

/** 既存OCR結果へ追従させる共有範囲（#2973） */
export interface ResultShareScopeUpdate {
  shareScope: ShareScope;
  sharedGroupIds: string[];
  sharedUserIds: string[];
}

/**
 * 指定パターンに紐づく既存OCR結果の共有範囲を一括で追従更新する（#2973）。
 *
 * 背景: OCR結果は実行時点のパターンの共有範囲（share_scope / shared_group_ids /
 * shared_user_ids）を結果ドキュメント自身にコピーして保持する。パターンの共有範囲を
 * 後から変更してもこの既存結果には伝播しないため、旧スコープ（多くは company）のまま残り、
 * 閲覧制御（canViewSharedResource）・担当者候補（#2927）が効かなくなる。本関数で
 * pattern_id 一致の結果を batch（500件区切り）で揃える。
 *
 * Algolia について: 検索インデックス（results_<env>）は share 系フィールドを保持しておらず、
 * 検索のアクセス制御は Firestore 側（rules / canViewSharedResource）で担保している。結果の
 * 書き込みで syncResultsToAlgolia（onDocumentWritten）は発火するが、レコード内容は share
 * フィールドを含まないため変化せず、明示的な Algolia 同期は不要。
 *
 * @returns 更新した結果ドキュメント数
 */
export const syncResultsShareScopeByPattern = async (
  patternId: string,
  update: ResultShareScopeUpdate,
): Promise<number> => {
  const resultsRef = collection(db, 'results');
  const snapshot = await getDocs(query(resultsRef, where('pattern_id', '==', patternId)));
  if (snapshot.empty) {
    return 0;
  }

  // group 以外（company/private）は共有先IDを持たないため空配列に正規化する
  const sharedGroupIds = update.shareScope === shareScope.group ? update.sharedGroupIds : [];
  const sharedUserIds = update.shareScope === shareScope.group ? update.sharedUserIds : [];

  const docs = snapshot.docs;
  for (let i = 0; i < docs.length; i += firestoreBatchLimit) {
    const batch = writeBatch(db);
    docs.slice(i, i + firestoreBatchLimit).forEach(resultDoc => {
      batch.update(doc(db, 'results', resultDoc.id), {
        share_scope: update.shareScope,
        shared_group_ids: sharedGroupIds,
        shared_user_ids: sharedUserIds,
      });
    });
    await batch.commit();
  }

  return docs.length;
};
